import { useEffect, useState } from "react";
import { CloudArrowUp, ArrowsClockwise, WifiSlash, WifiHigh } from "@phosphor-icons/react";
import { Queue, drainQueue, onQueueChange, initialQueueCount } from "../lib/offlineQueue";
import { Photos } from "../lib/api";
import { toast } from "sonner";

export const QueueBanner = () => {
  const [count, setCount] = useState(0);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [draining, setDraining] = useState(false);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    initialQueueCount().then(setCount);
    const off = onQueueChange((c) => setCount(c));
    const onOnline = () => {
      setOnline(true);
      tryDrain(true);
    };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      off();
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tryDrain = async (silent = false) => {
    if (draining) return;
    const c = await Queue.count();
    if (c === 0) {
      if (!silent) toast.message("Nothing pending");
      return;
    }
    setDraining(true);
    setProgress({ sent: 0, total: c });
    try {
      const res = await drainQueue(
        (sessionId, payload) => Photos.create(sessionId, payload),
        (st) => setProgress(st)
      );
      if (res.sent > 0) toast.success(`Synced ${res.sent} queued capture${res.sent > 1 ? "s" : ""}`);
      if (res.failed > 0) toast.error("Some uploads failed — will retry when online");
    } catch {
      toast.error("Drain failed");
    } finally {
      setDraining(false);
      setProgress(null);
    }
  };

  if (count === 0 && online) return null;

  return (
    <div
      className={`hairline-2 p-3 mb-4 flex items-center justify-between gap-3 ${
        online ? "bg-neutral-50" : "bg-black text-white"
      }`}
      style={{ boxShadow: "4px 4px 0 0 #FF2A2A" }}
      data-testid="queue-banner"
    >
      <div className="min-w-0 flex items-center gap-3">
        {online ? (
          <WifiHigh size={22} weight="bold" />
        ) : (
          <WifiSlash size={22} weight="bold" />
        )}
        <div className="min-w-0">
          <p className="label-mono">
            {online ? "PENDING UPLOADS" : "OFFLINE MODE"}
          </p>
          <p className="text-sm font-mono truncate">
            {draining && progress
              ? `Syncing ${progress.sent}/${progress.total} · ${progress.current || ""}`
              : `${count} capture${count === 1 ? "" : "s"} queued locally`}
          </p>
        </div>
      </div>
      <button
        onClick={() => tryDrain(false)}
        disabled={!online || draining || count === 0}
        className={`px-3 py-2 border-2 font-bold text-xs uppercase tracking-wider transition-transform active:scale-95 disabled:opacity-40 flex items-center gap-1.5 ${
          online
            ? "border-black bg-black text-white"
            : "border-white bg-white text-black"
        }`}
        data-testid="queue-retry-btn"
      >
        {draining ? (
          <ArrowsClockwise size={14} weight="bold" className="animate-spin" />
        ) : (
          <CloudArrowUp size={14} weight="bold" />
        )}
        {draining ? "Syncing" : "Retry"}
      </button>
    </div>
  );
};

export default QueueBanner;
